import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Crypto from 'expo-crypto';
import {Platform} from 'react-native';
import {track} from '../../services/analytics';
import {isSameWithdrawalIntentSignature, WithdrawalAttempt, WithdrawalIntentSignature} from './utils';

const legacyStorageVersion = 1;
const storageVersion = 2;
const legacyStorageKeyPrefix = 'withdrawal_attempts';
const storageKeyPrefix = 'withdrawal_attempt';

type StorageOperation = 'load' | 'save' | 'remove';
type StorageFailureReason = 'io' | 'corrupt';
type StorageSlot = 'primary' | 'backup';

type LegacyPersistedWithdrawalAttempts = {
  version: typeof legacyStorageVersion, attempts: Record<string, WithdrawalAttempt>
};

type PersistedWithdrawalAttemptRecord = {
  version: typeof storageVersion, revision: number, attempt: WithdrawalAttempt, retired?: true
};

type ValidSlot = { slot: StorageSlot, record: PersistedWithdrawalAttemptRecord };
type SlotReadResult = { slot: StorageSlot, raw: string | null, record?: PersistedWithdrawalAttemptRecord };
type CrossContextLockManager = {
  request: <T>(name: string, callback: () => Promise<T>) => Promise<T>
};

export class WithdrawalAttemptStoreError extends Error {
  constructor(readonly operation: StorageOperation, readonly reason: StorageFailureReason, cause?: unknown) {
    super(`Withdrawal attempt storage ${operation} failed`);
    this.name = 'WithdrawalAttemptStoreError';
    if (cause !== undefined) (this as Error & { cause?: unknown }).cause = cause;
  }
}

export const isWithdrawalAttemptStoreError = (error: unknown): error is WithdrawalAttemptStoreError => (
  error instanceof WithdrawalAttemptStoreError
);

export const isWithdrawalAttemptStoreCorruptionError = (error: unknown) => (
  isWithdrawalAttemptStoreError(error) && error.reason === 'corrupt'
);

const legacyStorageKey = (userId: string) => `${legacyStorageKeyPrefix}:v${legacyStorageVersion}:${userId}`;

const signatureKey = (signature: WithdrawalIntentSignature) => JSON.stringify([
  signature.amountCents, signature.paymentType, signature.destinationId
]);

const storageDigest = async (value: unknown, operation: StorageOperation) => {
  try {
    return await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, JSON.stringify(value));

  } catch (cause) {
    throw new WithdrawalAttemptStoreError(operation, 'io', cause);
  }
};

const intentStorageDigest = (userId: string, signature: WithdrawalIntentSignature, operation: StorageOperation) => storageDigest([userId, signatureKey(signature)], operation);

const recordStorageKey = async (userId: string, signature: WithdrawalIntentSignature, slot: StorageSlot, operation: StorageOperation) => `${storageKeyPrefix}:v${storageVersion}:${await intentStorageDigest(userId, signature, operation)}:${slot}`;

const runWithCrossContextLock = async <T>(name: string, storageOperation: StorageOperation, operation: () => Promise<T>): Promise<T> => {
  const lockManager = (globalThis as typeof globalThis & {
    navigator?: { locks?: CrossContextLockManager }
  }).navigator?.locks;
  if (lockManager?.request) return lockManager.request(name, operation);
  if (Platform.OS === 'web') throw new WithdrawalAttemptStoreError(storageOperation, 'io');
  return operation();
};

const isPaymentType = (value: unknown): value is WithdrawalIntentSignature['paymentType'] => (
  value === 'CARD' || value === 'SAME_DAY_ACH' || value === 'STANDARD_ACH'
);

const normalizePersistedAttempt = (value: unknown, allowMissingStatus: boolean): WithdrawalAttempt | undefined => {
  if (!value || typeof value !== 'object') return undefined;

  const attempt = value as Partial<WithdrawalAttempt>;
  const signature = attempt.signature as Partial<WithdrawalIntentSignature> | undefined;

  if (typeof attempt.idempotencyKey !== 'string' || !attempt.idempotencyKey || !signature || typeof signature.amountCents !== 'number' || !Number.isInteger(signature.amountCents) || signature.amountCents <= 0 || !isPaymentType(signature.paymentType) || typeof signature.destinationId !== 'string' || (signature.paymentType === 'CARD' ? !signature.destinationId : signature.destinationId !== '')) {
    return undefined;
  }

  if (attempt.status !== 'ready' && attempt.status !== 'uncertain' && !(allowMissingStatus && attempt.status === undefined)) return undefined;

  return {
    idempotencyKey: attempt.idempotencyKey,
    signature: signature as WithdrawalIntentSignature,
    // Records written before status existed may already have reached payoff.
    status: attempt.status === 'ready' ? 'ready' : 'uncertain'
  };
};

const reportCorruption = (operation: StorageOperation, source: 'legacy' | StorageSlot | 'slot-conflict') => {
  console.warn('withdrawal attempt storage corruption', {operation, source});
  try {
    track('withdrawal_attempt_storage_corruption', {operation, source});
  } catch {
    // Observability must not change the fail-closed storage result.
  }
};

const corruptionError = (operation: StorageOperation, source: 'legacy' | StorageSlot | 'slot-conflict', cause?: unknown) => {
  reportCorruption(operation, source);
  return new WithdrawalAttemptStoreError(operation, 'corrupt', cause);
};

const readRaw = async (key: string, operation: StorageOperation) => {
  try {
    return await AsyncStorage.getItem(key);
  } catch (cause) {
    throw new WithdrawalAttemptStoreError(operation, 'io', cause);
  }
};

const removeRaw = async (key: string, operation: StorageOperation) => {
  try {
    await AsyncStorage.removeItem(key);
  } catch (cause) {
    throw new WithdrawalAttemptStoreError(operation, 'io', cause);
  }
};

const parseLegacyStore = (raw: string, operation: StorageOperation): LegacyPersistedWithdrawalAttempts => {
  try {
    const value = JSON.parse(raw) as Partial<LegacyPersistedWithdrawalAttempts>;
    if (value.version !== legacyStorageVersion || !value.attempts || typeof value.attempts !== 'object' ||
      Array.isArray(value.attempts)) {
      throw new Error('Unsupported legacy withdrawal attempt storage value');
    }

    const attempts: Record<string, WithdrawalAttempt> = {};
    for (const [key, attempt] of Object.entries(value.attempts)) {
      const normalizedAttempt = normalizePersistedAttempt(attempt, true);
      if (!normalizedAttempt || key !== signatureKey(normalizedAttempt.signature)) {
        throw new Error('Invalid legacy withdrawal attempt storage value');
      }
      attempts[key] = normalizedAttempt;
    }

    return {version: legacyStorageVersion, attempts};
  } catch (cause) {
    throw corruptionError(operation, 'legacy', cause);
  }
};

const parseRecord = (raw: string, expectedSignature: WithdrawalIntentSignature, operation: StorageOperation, slot: StorageSlot): PersistedWithdrawalAttemptRecord => {
  try {
    const value = JSON.parse(raw) as Partial<PersistedWithdrawalAttemptRecord>;
    const attempt = normalizePersistedAttempt(value.attempt, false);

    if (value.version !== storageVersion || typeof value.revision !== 'number' || !Number.isInteger(value.revision) || value.revision <= 0 || !attempt || (value.retired !== undefined && value.retired !== true) || !isSameWithdrawalIntentSignature(attempt.signature, expectedSignature)) {
      throw new Error('Invalid withdrawal attempt record');
    }

    return {
      version: storageVersion, revision: value.revision, attempt,
      ...(value.retired === true ? {retired: true as const} : {})
    };

  } catch (cause) {
    throw corruptionError(operation, slot, cause);
  }
};

const readSlot = async (userId: string, signature: WithdrawalIntentSignature, slot: StorageSlot, operation: StorageOperation): Promise<SlotReadResult> => {
  const raw = await readRaw(await recordStorageKey(userId, signature, slot, operation), operation);
  if (raw === null) return {slot, raw};

  try {
    return {slot, raw, record: parseRecord(raw, signature, operation, slot)};

  } catch (error) {
    if (!isWithdrawalAttemptStoreCorruptionError(error)) throw error;
    return {slot, raw};
  }
};

const selectLatestValidSlot = (slots: SlotReadResult[], operation: StorageOperation): ValidSlot | undefined => {
  const validSlots = slots.filter((slot): slot is SlotReadResult & {
    record: PersistedWithdrawalAttemptRecord
  } => (!!slot.record));
  const corruptSlots = slots.filter(slot => slot.raw !== null && !slot.record);

  if (validSlots.length === 0) {
    if (corruptSlots.length > 0) throw new WithdrawalAttemptStoreError(operation, 'corrupt');
    return undefined;
  }

  if (validSlots.length === 2) {
    const orderedSlots = [...validSlots].sort((left, right) => left.record.revision - right.record.revision);
    const [older, newer] = orderedSlots;
    const sameRecord = JSON.stringify(older.record) === JSON.stringify(newer.record);
    const sameKey = older.record.attempt.idempotencyKey === newer.record.attempt.idempotencyKey;
    const isControlledKeyReplacement = older.record.retired === true && newer.record.retired !== true && newer.record.revision === older.record.revision + 1;
    const resurrectsRetiredKey = sameKey && older.record.retired === true && newer.record.retired !== true;

    if ((older.record.revision === newer.record.revision && !sameRecord) ||
      resurrectsRetiredKey || (!sameKey && !isControlledKeyReplacement)) {
      throw corruptionError(operation, 'slot-conflict');
    }
  }

  const latest = [...validSlots].sort((left, right) => right.record.revision - left.record.revision)[0];
  // A corrupt slot may contain a newer uncertain state. Only an already-uncertain
  // or retired survivor is safe to recover without risking a duplicate withdrawal.
  if (corruptSlots.length > 0 && latest.record.attempt.status !== 'uncertain' && !latest.record.retired) {
    throw new WithdrawalAttemptStoreError(operation, 'corrupt');
  }

  return {slot: latest.slot, record: latest.record};
};

const readAttemptSlots = async (userId: string, signature: WithdrawalIntentSignature, operation: StorageOperation) => {
  const slots = await Promise.all((['primary', 'backup'] as StorageSlot[]).map(slot => (
    readSlot(userId, signature, slot, operation)
  )));
  return {slots, latest: selectLatestValidSlot(slots, operation)};
};

const writeAndVerifySlot = async (userId: string, signature: WithdrawalIntentSignature, slot: StorageSlot, record: PersistedWithdrawalAttemptRecord, operation: StorageOperation) => {
  const key = await recordStorageKey(userId, signature, slot, operation);
  const raw = JSON.stringify(record);

  try {
    await AsyncStorage.setItem(key, raw);

  } catch (cause) {
    throw new WithdrawalAttemptStoreError(operation, 'io', cause);
  }

  const persistedRaw = await readRaw(key, operation);
  if (!persistedRaw) throw corruptionError(operation, slot);
  const persistedRecord = parseRecord(persistedRaw, signature, operation, slot);
  if (JSON.stringify(persistedRecord) !== JSON.stringify(record)) {
    throw corruptionError(operation, slot);
  }
};

const writeAndVerifySlotWithRetry = async (userId: string, signature: WithdrawalIntentSignature, slot: StorageSlot, record: PersistedWithdrawalAttemptRecord, operation: StorageOperation) => {
  try {
    await writeAndVerifySlot(userId, signature, slot, record, operation);
  } catch {
    await writeAndVerifySlot(userId, signature, slot, record, operation);
  }
};

const writeAttemptRecord = async (userId: string, attempt: WithdrawalAttempt, operation: StorageOperation, expectedCurrentStatus?: WithdrawalAttempt['status']) => {
  const {latest} = await readAttemptSlots(userId, attempt.signature, operation);
  if (expectedCurrentStatus && (!latest || latest.record.retired ||
    latest.record.attempt.idempotencyKey !== attempt.idempotencyKey ||
    latest.record.attempt.status !== expectedCurrentStatus)) {
    throw corruptionError(operation, 'slot-conflict');
  }
  if (latest) {
    const sameKey = latest.record.attempt.idempotencyKey === attempt.idempotencyKey;
    if ((!latest.record.retired && !sameKey) || (latest.record.retired && sameKey)) {
      throw corruptionError(operation, 'slot-conflict');
    }
    if (!expectedCurrentStatus && !latest.record.retired && sameKey &&
      latest.record.attempt.status === 'uncertain' && attempt.status === 'ready') {
      throw corruptionError(operation, 'slot-conflict');
    }
  }

  const record: PersistedWithdrawalAttemptRecord = {
    version: storageVersion, revision: (latest?.record.revision || 0) + 1, attempt
  };

  if (!latest) {
    await writeAndVerifySlot(userId, attempt.signature, 'primary', record, operation);
    await writeAndVerifySlot(userId, attempt.signature, 'backup', record, operation);
    return;
  }

  const targetSlot: StorageSlot = latest.slot === 'primary' ? 'backup' : 'primary';
  await writeAndVerifySlot(userId, attempt.signature, targetSlot, record, operation);
};

const writeRetiredRecord = async (userId: string, latest: ValidSlot, operation: StorageOperation) => {
  const record: PersistedWithdrawalAttemptRecord = {
    version: storageVersion, revision: latest.record.revision + 1, attempt: latest.record.attempt, retired: true
  };
  let writeError: unknown;

  for (const slot of ['primary', 'backup'] as StorageSlot[]) {
    try {
      await writeAndVerifySlotWithRetry(userId, record.attempt.signature, slot, record, operation);

    } catch (error) {
      if (writeError === undefined) writeError = error;
    }
  }
  if (writeError !== undefined) throw writeError;
};

const migrationPromises = new Map<string, Promise<void>>();
const operationQueues = new Map<string, Promise<void>>();

const runForIntent = async <T>(userId: string, signature: WithdrawalIntentSignature, storageOperation: StorageOperation, operation: () => Promise<T>): Promise<T> => {
  const digest = await intentStorageDigest(userId, signature, storageOperation);
  const key = `${storageKeyPrefix}:v${storageVersion}:${digest}`;
  const previous = operationQueues.get(key) || Promise.resolve();
  const current = previous.then(() => runWithCrossContextLock(key, storageOperation, operation));
  const tail = current.then(() => undefined, () => undefined);
  operationQueues.set(key, tail);

  try {
    return await current;
  } finally {
    if (operationQueues.get(key) === tail) operationQueues.delete(key);
  }
};

const migrateLegacyStore = async (userId: string, operation: StorageOperation) => {
  const raw = await readRaw(legacyStorageKey(userId), operation);
  if (raw === null) return;

  const legacyStore = parseLegacyStore(raw, operation);
  for (const attempt of Object.values(legacyStore.attempts)) {
    const {slots, latest} = await readAttemptSlots(userId, attempt.signature, operation);
    if (!latest) {
      await writeAttemptRecord(userId, attempt, operation);
      continue;
    }

    if (latest.record.attempt.idempotencyKey !== attempt.idempotencyKey) {
      throw corruptionError(operation, 'slot-conflict');
    }

    if (latest.record.retired) {
      await writeRetiredRecord(userId, latest, operation);
      continue;
    }

    const validSlotCount = slots.filter(slot => !!slot.record).length;
    if (validSlotCount < 2 || latest.record.attempt.status !== 'uncertain') {
      await writeAttemptRecord(userId, {...latest.record.attempt, status: 'uncertain'}, operation);
    }
  }
  await removeRaw(legacyStorageKey(userId), operation);
};

const ensureLegacyStoreMigrated = async (userId: string, operation: StorageOperation) => {
  const existing = migrationPromises.get(userId);
  if (existing) return existing;

  const migration = storageDigest(['legacy-migration', userId], operation).then(digest => {
    return runWithCrossContextLock(`${storageKeyPrefix}:legacy-migration:${digest}`, operation, () => migrateLegacyStore(userId, operation));

  }).finally(() => {
    if (migrationPromises.get(userId) === migration) migrationPromises.delete(userId);
  });
  migrationPromises.set(userId, migration);
  return migration;
};

export const loadWithdrawalAttempt = async (userId: string, signature: WithdrawalIntentSignature): Promise<WithdrawalAttempt | undefined> => runForIntent(userId, signature, 'load', async () => {
  await ensureLegacyStoreMigrated(userId, 'load');
  const {latest} = await readAttemptSlots(userId, signature, 'load');
  return latest?.record.retired ? undefined : latest?.record.attempt;
});

export const saveWithdrawalAttempt = async (userId: string, attempt: WithdrawalAttempt) => runForIntent(
  userId, attempt.signature, 'save', async () => {
    await ensureLegacyStoreMigrated(userId, 'save');
    await writeAttemptRecord(userId, attempt, 'save');
  }
);

export const restoreWithdrawalAttemptAsReady = async (userId: string, attempt: WithdrawalAttempt) => runForIntent(
  userId, attempt.signature, 'save', async () => {
    await ensureLegacyStoreMigrated(userId, 'save');
    await writeAttemptRecord(userId, {...attempt, status: 'ready'}, 'save', 'uncertain');
  }
);

export const removeWithdrawalAttempt = async (userId: string, attempt: WithdrawalAttempt) => runForIntent(
  userId, attempt.signature, 'remove', async () => {
    await ensureLegacyStoreMigrated(userId, 'remove');
    const {latest} = await readAttemptSlots(userId, attempt.signature, 'remove');
    if (!latest || latest.record.attempt.idempotencyKey !== attempt.idempotencyKey) return;

    await writeRetiredRecord(userId, latest, 'remove');

    let cleanupError: unknown;
    for (const slot of ['primary', 'backup'] as StorageSlot[]) {
      try {
        const key = await recordStorageKey(userId, attempt.signature, slot, 'remove');
        await removeRaw(key, 'remove').catch(() => removeRaw(key, 'remove'));
      } catch (error) {
        if (cleanupError === undefined) cleanupError = error;
      }
    }
    if (cleanupError !== undefined) throw cleanupError;
  }
);
